import React, { useState, useEffect } from 'react';
import { Toaster } from 'sonner';
import { api, AuthContext } from './api';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { Sidebar } from './components/layout/Sidebar';
import { MissingScheduleModal } from './components/modals/WorkshopScheduleModals';
import { ChatbotWidget } from './components/widgets/ChatbotWidget';
import { FeedbackWidget } from './components/widgets/FeedbackWidget';
import AuthenticateUser from './pages/AuthenticateUser';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import CourseBrowser from './pages/CourseBrowser';
import ContentUpload from './pages/ContentUpload';
import ManageCourses from './pages/ManageCourses';
import ChatbotInsights from './pages/ChatbotInsights';
import FeedbackAnalytics from './pages/FeedbackAnalytics';
import UserManagement from './pages/UserManagement';
import Analytics from './pages/Analytics';
import WorkshopsManagement from './pages/WorkshopsManagement';
import PrivateWorkshopRequestsAdmin from './pages/PrivateWorkshopRequestsAdmin';
import LabRequestsPage from './pages/LabRequestsPage';
import EmailSettings from './pages/EmailSettings';
import AccessControl from './pages/AccessControl';
import TrainerAnalytics from './pages/TrainerAnalytics';
import WorkshopPlanner from './pages/WorkshopPlanner';
import WhatsNewPage from './pages/WhatsNewPage';
import AboutPage from './pages/AboutPage';
import PartnerAdminSetup from './pages/PartnerAdminSetup';
import PartnerAdminDashboard from './pages/PartnerAdminDashboard';
import TrainerManagement from './pages/TrainerManagement';
import SyndicationRequestsAdmin from './pages/SyndicationRequestsAdmin';
import ServerInfoPage from './pages/ServerInfoPage';
import MyLabRequestsPage from './pages/MyLabRequestsPage';
import DeletedItemsPage from './pages/DeletedItemsPage';
import AccountAuditPage from './pages/AccountAuditPage';
import MyPrivateWorkshopRequestsPage from './pages/MyPrivateWorkshopRequestsPage';
import MySyndicationRequestPage from './pages/MySyndicationRequestPage';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPartnerSetup, setShowPartnerSetup] = useState(false);
  const [showAboutPage, setShowAboutPage] = useState(false);
  const [showMissingScheduleModal, setShowMissingScheduleModal] = useState(false);
  const [missingScheduleCourses, setMissingScheduleCourses] = useState([]);

  // Function to check for missing schedules
  const checkMissingSchedules = async () => {
    try {
      const result = await api.request('/courses/missing-schedule');
      if (result.courses && result.courses.length > 0) {
        setMissingScheduleCourses(result.courses);
        setShowMissingScheduleModal(true);
      }
    } catch (err) {
      console.error('Failed to check missing schedules:', err);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        api.setToken(token);
        try {
          const userData = await api.request('/auth/me');
          setUser(userData);
          // Check if partner_admin needs setup
          if (userData.role === 'partner_admin' && userData.setup_completed === false) {
            setShowPartnerSetup(true);
          } else if (['training_partner', 'partner_admin'].includes(userData.role)) {
            // Check for missing schedules after successful auth (for partners)
            setTimeout(() => checkMissingSchedules(), 1000);
          }
        } catch (err) {
          api.setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if accessing /server-info route (after hooks to avoid rules-of-hooks violation)
  if (window.location.pathname === '/server-info') {
    return <ServerInfoPage />;
  }

  // Handle LevelUp SSO callback
  if (window.location.pathname === '/authenticate-user') {
    return <AuthenticateUser />;
  }

  const handleLogin = (userData) => {
    setUser(userData);
    // Check if partner_admin needs setup
    if (userData.role === 'partner_admin' && userData.setup_completed === false) {
      setShowPartnerSetup(true);
    } else if (['training_partner', 'partner_admin'].includes(userData.role)) {
      setTimeout(() => checkMissingSchedules(), 1000);
    }
  };

  const handleTermsAccept = () => {
    setUser(prev => ({ ...prev, terms_accepted: true }));
  };

  const handlePartnerSetupComplete = () => {
    setShowPartnerSetup(false);
    setUser(prev => ({ ...prev, setup_completed: true }));
    setActiveView('partner-dashboard');
  };

  const handlePartnerSetupSkip = async () => {
    try {
      await api.request('/partner-admin/skip-setup', { method: 'PUT' });
      setShowPartnerSetup(false);
      setActiveView('partner-dashboard');
    } catch (err) {
      console.error('Failed to skip setup:', err);
    }
  };

  const handleLogout = () => {
    api.setToken(null);
    setUser(null);
    setActiveView('dashboard');
    setShowPartnerSetup(false);
    setShowAboutPage(false);
    window.location.href = 'https://skillupwithlevelup.com';
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-16 h-16 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white mt-4 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const isLocalLogin = window.location.pathname === '/local-login';
    return <LoginPage onLogin={handleLogin} isLocalLogin={isLocalLogin} />;
  }

  // Show partner admin setup wizard if needed
  if (showPartnerSetup && user.role === 'partner_admin') {
    return (
      <PartnerAdminSetup 
        user={user} 
        onComplete={handlePartnerSetupComplete} 
        onSkip={handlePartnerSetupSkip}
      />
    );
  }

  const renderView = () => {
    // Handle About page
    if (showAboutPage) {
      return <AboutPage onBack={() => setShowAboutPage(false)} />;
    }

    switch (activeView) {
      case 'home':
      case 'dashboard':
        // Default dashboard (welcome page) for all roles
        return <Dashboard user={user} onNavigate={(view) => setActiveView(view)} />;
      case 'partner-dashboard':
      case 'partner-analytics':
        // Analytics dashboard specifically for partner_admin
        return <PartnerAdminDashboard user={user} onNavigate={(view) => setActiveView(view)} />;
      case 'trainer-management':
        return <TrainerManagement user={user} />;
      case 'courses':
        return <CourseBrowser user={user} onNavigate={(view) => setActiveView(view)} onTermsAccept={handleTermsAccept} />;
      case 'upload':
        return <ContentUpload user={user} />;
      case 'manage-courses':
        return <ManageCourses />;
      case 'chatbot-insights':
        return <ChatbotInsights />;
      case 'feedback-analytics':
        return <FeedbackAnalytics />;
      case 'users':
        return <UserManagement currentUser={user} />;
      case 'analytics':
        return <Analytics onNavigate={(view) => setActiveView(view)} user={user} />;
      case 'workshops':
      case 'my-workshops':
        return <WorkshopsManagement user={user} />;
      case 'workshop-requests':
        return <PrivateWorkshopRequestsAdmin user={user} />;
      case 'lab-requests':
        return <LabRequestsPage user={user} />;
      case 'email-settings':
        return <EmailSettings />;
      case 'access-control':
        return <AccessControl />;
      case 'deleted-items':
        return <DeletedItemsPage user={user} />;
      case 'account-audit':
        return <AccountAuditPage />;
      case 'my-analytics':
        return <TrainerAnalytics />;
      case 'workshop-planner':
        return <WorkshopPlanner user={user} />;
      case 'whats-new':
        return <WhatsNewPage user={user} />;
      case 'syndication-requests':
        return <SyndicationRequestsAdmin user={user} />;
      case 'my-lab-requests':
        return <MyLabRequestsPage user={user} />;
      case 'my-workshop-requests':
        return <MyPrivateWorkshopRequestsPage user={user} />;
      case 'my-syndication-requests':
        return <MySyndicationRequestPage />;
      case 'about':
        return <AboutPage onBack={() => setActiveView('home')} />;
      default:
        // Default dashboard (welcome page) for all roles
        return <Dashboard user={user} onNavigate={(view) => setActiveView(view)} />;
    }
  };

  const handleHomeClick = () => {
    setShowAboutPage(false);
    setActiveView('home');
  };

  return (
    <>
      <AuthContext.Provider value={{ user, setUser }}>
      <div className="h-screen bg-levelup-light flex flex-col overflow-hidden">
        <Navbar 
          user={user} 
          onLogout={handleLogout} 
          onMenuClick={() => setSidebarOpen(true)} 
          onHomeClick={handleHomeClick}
        />
        <div className="flex flex-1 overflow-hidden">
          <Sidebar 
            user={user} 
            activeView={activeView} 
            setActiveView={(view) => { setShowAboutPage(false); setActiveView(view); }}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
          <main className="flex-1 overflow-y-auto p-3 lg:p-4 flex flex-col">
            <div className="flex-1">
              {renderView()}
            </div>
            <Footer onAboutClick={() => setShowAboutPage(true)} onHomeClick={handleHomeClick} />
          </main>
        </div>
        {/* AI Chatbot */}
        <ChatbotWidget />
        {/* Feedback Widget for Partners */}
        {['training_partner', 'partner_admin'].includes(user.role) && <FeedbackWidget />}
        {/* Missing Schedule Warning Modal */}
        {showMissingScheduleModal && missingScheduleCourses.length > 0 && (
          <MissingScheduleModal 
            courses={missingScheduleCourses}
            onSchedule={() => {
              setShowMissingScheduleModal(false);
              setActiveView('my-workshops');
            }}
            onClose={() => setShowMissingScheduleModal(false)}
          />
        )}
      </div>
      </AuthContext.Provider>
      <Toaster richColors position="top-right" />
    </>
  );
}

export default App;
